import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  useImperativeHandle,
} from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { XMarkIcon } from "@heroicons/react/24/outline"

const MessageDialog = forwardRef((props, ref) => {
  const [isOpen, setIsOpen] = useState(false)
  const [state, setState] = useState({
    title: "",
    message: "",
    onConfirm: () => {},
    onClose: () => {},
    btnLabel: "",
    showCloseX: true,
  })

  const show = ({ title, message, onConfirm, onClose, btnLabel, showCloseX = true }) => {
    setIsOpen(true)
    setState({
      title,
      message,
      onConfirm,
      onClose,
      btnLabel,
      showCloseX,
    })
  }

  const { title, message, onConfirm, onClose, btnLabel, showCloseX } = state

  useImperativeHandle(ref, () => ({
    show,
  }))

  useEffect(() => {
    if (!isOpen && onClose) {
      onClose()
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center"
      style={{ zIndex: 1000 }}
    >
      <div className="bg-admin_paper rounded-lg shadow-lg w-[340px] sm:w-[400px] p-6 relative">
        {showCloseX && (
          <button
            className="absolute top-4 right-4 text-admin_text_secondary hover:text-admin_text transition-colors"
            onClick={() => {
              setIsOpen(false)
              if (onClose) onClose()
            }}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        )}

        <Typography variant="admin_h3" className="mb-4 pr-8">
          {title}
        </Typography>

        <hr className="border-t border-admin_bordered mb-4" />

        {typeof message === 'string' ? (
          <Typography className="mb-6 text-base text-admin_text_secondary">
            {message}
          </Typography>
        ) : (
          <div className="mb-6">
            {message}
          </div>
        )}
        
        {btnLabel && (
          <div className="flex justify-center space-x-4 mt-5">
            <Button
              variant="admin_primary"
              className="py-2 px-6 rounded-full"
              onClick={() => {
                setIsOpen(false)
                if (onConfirm) onConfirm()
              }}
            >
              {btnLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
})

let messageInstance = null

export const showMessageDialog = (options) => {
  if (messageInstance === null) return
  messageInstance.show(options)
}

export const showMessageDialogAsync = (options) => {
  return new Promise((resolve) => {
    showMessageDialog({
      ...options,
      onConfirm: () => resolve(true),
    })
  })
}

const MessageDialogProvider = () => {
  const messageRef = useRef()

  useEffect(() => {
    messageInstance = messageRef.current
    return () => {
      messageInstance = null
    }
  }, [])

  return <MessageDialog ref={messageRef} />
}

export default MessageDialogProvider
